import { isNaN } from 'lodash-es';

import { ColorToken } from '../../core/types/color-palette.js';
import {
  Dimension,
  CategoricalBarData,
} from '../types/categorical-bar-chart-data.js';
import { CategoricalBarChartData } from '../types/categorical-bar-chart.js';

const getRelativeValue = (value: number, total: number): number =>
  (value * 100) / total;

const getTotalValue = (categoryData: CategoricalBarChartData): number =>
  Object.values(categoryData.value).reduce(
    (total, dimensionValue) => total + Math.abs(dimensionValue),
    0,
  );

const buildDimension = (name: string, value: number): Dimension => ({
  id: crypto.randomUUID(),
  name,
  value,
});

const getValue = (
  value: unknown,
  isRelative: boolean,
  totalValue: number,
): number => {
  const numericValue = Number(value);
  const numberValue = isNaN(numericValue) ? 0 : numericValue;

  return isRelative ? getRelativeValue(numberValue, totalValue) : numberValue;
};

/**
 * Get dimensions range value and dimensions from category data. if the value is number, it will return an Array of 1 element.
 * Otherwise, it will return an Array of N elements if the value is an object
 * @internal
 * @param isRelative - is relative value
 * @param getColorToken - get token base on index
 * @param data - original chart data array
 * @param categoryIndex - index of the given category
 */
function getCategoryDimensions(
  data: CategoricalBarChartData[],
  categoryIndex: number,
  isRelative: boolean,
): Dimension[] {
  const categoryData = data[categoryIndex];
  if (typeof categoryData.value === 'object') {
    const totalValue = getTotalValue(categoryData);

    return Object.keys(categoryData.value).map((dimensionKey) => {
      const value = getValue(
        (categoryData.value as Record<string, number>)[dimensionKey],
        isRelative,
        totalValue,
      );

      return buildDimension(dimensionKey, value);
    });
  }

  const totalValue = Math.max(...data.map((d) => d.value as number));
  const value = getValue(categoryData.value, isRelative, totalValue);

  return [buildDimension(categoryData.category, value)];
}

/**
 * Builds the categorical bar chart categories and returns the built categories, the chart unit and colors overrides.
 * Only categories with the same unit as the first series unit are considered.
 *
 * @param data - categorical chart data
 * @param isRelative - display values as relatives
 * @param isSequentialColor - either each bar has one color of the palette or they all share a single color
 */
export function buildCategories(
  data: CategoricalBarChartData[],
  isRelative: boolean,
): [CategoricalBarData[], string | undefined, Record<string, ColorToken>] {
  // The first series dictates the unit to be used
  const unit = data[0]?.unit;
  const categories: CategoricalBarData[] = [];
  const overrideColors: Record<string, ColorToken> = {};

  for (let categoryIndex = 0; categoryIndex < data.length; categoryIndex++) {
    const categoryData = data[categoryIndex];

    const isSingleDimension = typeof categoryData.value === 'number';

    if (isSingleDimension && categoryData.color) {
      overrideColors[categoryData.category] = {
        Default: categoryData.color,
      };
    }

    if (categoryData.unit === unit) {
      const dimensions = getCategoryDimensions(data, categoryIndex, isRelative);

      categories.push({
        id: crypto.randomUUID(),
        name: categoryData.category,
        dimensions,
      });
    }
  }

  return [categories, unit, overrideColors];
}
